import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Rocket,
  BarChart3,
  Zap,
  Target,
  ArrowRight,
  ArrowLeft,
  Check,
  TrendingUp,
  LayoutDashboard,
  BookOpen,
  Sparkles,
  Scale,
} from "lucide-react";
import { API_URL } from "@/lib/api";
import { formatDateToYMD } from "@/lib/date";

const API = API_URL;
const ONBOARDING_KEY = "onboarding_completed";

const steps = [
  {
    id: "welcome",
    title: "Welcome to Trading Track",
    subtitle: "Your professional trading journal",
    icon: Rocket,
    color: "#1877F2",
  },
  {
    id: "strategy",
    title: "Create Your First Strategy",
    subtitle: "Organize trades by strategy",
    icon: Target,
    color: "#f97316",
  },
  {
    id: "trade",
    title: "Record Your First Trade",
    subtitle: "Start tracking your performance",
    icon: Zap,
    color: "#22c55e",
  },
  {
    id: "tour",
    title: "Dashboard Tour",
    subtitle: "Know where everything is",
    icon: LayoutDashboard,
    color: "#a855f7",
  },
];

function StepIndicator({ currentStep, totalSteps }) {
  return (
    <div className="flex items-center gap-2 justify-center mb-6">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div key={i} className="flex items-center">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              i === currentStep
                ? "w-8 bg-[#1877F2]"
                : i < currentStep
                ? "w-2 bg-[#1877F2]/60"
                : "w-2 bg-muted"
            }`}
          />
          {i < totalSteps - 1 && (
            <div
              className={`h-px w-4 ${
                i < currentStep ? "bg-[#1877F2]/40" : "bg-muted"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function WelcomeStep() {
  return (
    <div className="text-center space-y-4">
      <div className="mx-auto h-20 w-20 rounded-2xl bg-gradient-to-br from-[#1877F2] to-blue-700 flex items-center justify-center shadow-xl">
        <Rocket className="h-10 w-10 text-white" />
      </div>
      <div>
        <h3 className="text-2xl font-bold mb-2">Welcome to Trading Track!</h3>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          Your professional trading journal. Track trades, analyze performance,
          and improve your strategy with data-driven insights.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-4 mt-6">
        {[
          { icon: BarChart3, label: "Analytics", desc: "Deep performance insights" },
          { icon: BookOpen, label: "Journal", desc: "Log every trade" },
          { icon: TrendingUp, label: "Growth", desc: "Track your equity curve" },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + i * 0.1 }}
            className="p-3 rounded-xl bg-secondary/50 border border-border/50"
          >
            <item.icon className="h-5 w-5 text-[#1877F2] mx-auto mb-1" />
            <p className="text-xs font-semibold">{item.label}</p>
            <p className="text-[10px] text-muted-foreground">{item.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function StrategyStep({ form, setForm, errors }) {
  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg mb-3">
          <Target className="h-7 w-7 text-white" />
        </div>
        <h3 className="text-xl font-bold">Name Your Strategy</h3>
        <p className="text-muted-foreground text-sm">
          Give your trading strategy a name to keep things organized.
        </p>
      </div>
      <div className="space-y-3">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Strategy Name</Label>
          <Input
            placeholder="e.g. Scalping EUR/USD, Swing Strategy..."
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            className={`h-11 ${errors.name ? "border-destructive" : ""}`}
          />
          {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Currency</Label>
            <Input
              placeholder="USD"
              value={form.currency}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }))
              }
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Initial Capital</Label>
            <Input
              type="number"
              step="0.01"
              value={form.initial_capital}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  initial_capital: parseFloat(e.target.value) || 0,
                }))
              }
              className="h-11"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TradeStep({ tradeForm, setTradeForm }) {
  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg mb-3">
          <Zap className="h-7 w-7 text-white" />
        </div>
        <h3 className="text-xl font-bold">Add Your First Trade</h3>
        <p className="text-muted-foreground text-sm">
          Log your first trade to start building your track record.
        </p>
      </div>
      <div className="space-y-3">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Currency Pair</Label>
          <Input
            placeholder="EUR/USD"
            value={tradeForm.currency_pair}
            onChange={(e) =>
              setTradeForm((prev) => ({ ...prev, currency_pair: e.target.value }))
            }
            className="h-11"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Risk Amount</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="100"
              value={tradeForm.risk_amount}
              onChange={(e) =>
                setTradeForm((prev) => ({ ...prev, risk_amount: e.target.value }))
              }
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">P/L</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="50 or -50"
              value={tradeForm.profit_loss}
              onChange={(e) =>
                setTradeForm((prev) => ({ ...prev, profit_loss: e.target.value }))
              }
              className="h-11"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Date</Label>
          <Input
            type="date"
            value={tradeForm.execution_date}
            onChange={(e) =>
              setTradeForm((prev) => ({ ...prev, execution_date: e.target.value }))
            }
            className="h-11"
          />
        </div>
      </div>
    </div>
  );
}

function TourStep() {
  const tourItems = [
    {
      icon: LayoutDashboard,
      title: "Dashboard",
      desc: "Quick trade entry, equity curve, and recent trades",
      shortcut: "⌘K for command palette",
    },
    {
      icon: BarChart3,
      title: "Statistics",
      desc: "Win rate, profit factor, streaks, and heatmaps",
      shortcut: "Export CSV/PDF reports",
    },
    {
      icon: BookOpen,
      title: "Trades",
      desc: "Full trade list with filters and search",
      shortcut: "Click any trade to edit",
    },
    {
      icon: Target,
      title: "Calendar",
      desc: "Visual calendar of daily P/L",
      shortcut: "Navigate months easily",
    },
    {
      icon: Scale,
      title: "Compare",
      desc: "Execution gap between backtest and live",
      shortcut: "Link a strategy from the other mode",
    },
    {
      icon: Sparkles,
      title: "Quick Entry",
      desc: "Floating button on every page for fast trade logging",
      shortcut: "⌘⇧T shortcut available",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg mb-3">
          <LayoutDashboard className="h-7 w-7 text-white" />
        </div>
        <h3 className="text-xl font-bold">Dashboard Tour</h3>
        <p className="text-muted-foreground text-sm">
          Here's where to find everything you need.
        </p>
      </div>
      <div className="space-y-2">
        {tourItems.map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 + i * 0.08 }}
            className="flex items-center gap-3 p-3 rounded-xl bg-secondary/30 border border-border/50"
          >
            <div className="h-8 w-8 rounded-lg bg-[#1877F2]/15 flex items-center justify-center flex-shrink-0">
              <item.icon className="h-4 w-4 text-[#1877F2]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{item.title}</p>
              <p className="text-xs text-muted-foreground">{item.desc}</p>
            </div>
            <span className="text-[10px] text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded flex-shrink-0 hidden sm:block">
              {item.shortcut}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function OnboardingFlow({ mode, user, strategies, onOnboardingComplete }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [creating, setCreating] = useState(false);

  const [strategyForm, setStrategyForm] = useState({
    name: "",
    currency: "USD",
    initial_capital: 10000,
  });
  const [strategyErrors, setStrategyErrors] = useState({});

  const [tradeForm, setTradeForm] = useState({
    currency_pair: "",
    risk_amount: "",
    profit_loss: "",
    execution_date: formatDateToYMD(new Date()),
  });

  // Check if onboarding should show
  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed) {
      setOpen(true);
    }
  }, []);

  const completeOnboarding = useCallback(() => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setOpen(false);
    if (onOnboardingComplete) onOnboardingComplete();
  }, [onOnboardingComplete]);

  const handleNext = useCallback(() => {
    if (currentStep === 1) {
      // Validate strategy name
      if (!strategyForm.name.trim()) {
        setStrategyErrors({ name: "Strategy name is required" });
        return;
      }
      setStrategyErrors({});
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      completeOnboarding();
    }
  }, [currentStep, strategyForm.name, completeOnboarding]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  }, [currentStep]);

  const handleCreateStrategy = useCallback(async () => {
    if (!strategyForm.name.trim()) {
      setStrategyErrors({ name: "Strategy name is required" });
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/strategies`,
        { ...strategyForm, mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Strategy created!");
      return response.data;
    } catch (error) {
      toast.error("Error creating strategy");
      return null;
    } finally {
      setCreating(false);
    }
  }, [strategyForm, mode]);

  const handleCreateTrade = useCallback(
    async (strategyId) => {
      if (!tradeForm.currency_pair || !tradeForm.risk_amount) {
        toast.error("Fill in at least pair and risk amount");
        return;
      }

      try {
        const token = localStorage.getItem("token");
        await axios.post(
          `${API}/trades?mode=${mode}`,
          {
            currency_pair: tradeForm.currency_pair,
            strategy_id: strategyId,
            risk_amount: parseFloat(tradeForm.risk_amount),
            profit_loss: parseFloat(tradeForm.profit_loss) || 0,
            execution_date: tradeForm.execution_date,
            notes: "",
            attachments: [],
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("First trade recorded!");
      } catch (error) {
        toast.error("Error recording trade (you can add it later)");
      }
    },
    [tradeForm, mode]
  );

  const handleSkipToEnd = useCallback(() => {
    completeOnboarding();
  }, [completeOnboarding]);

  const handleFinishStep = useCallback(async () => {
    if (currentStep === 1) {
      // Create strategy on this step
      const strategy = await handleCreateStrategy();
      if (strategy) {
        setCurrentStep(2); // Move to trade step
      }
    } else if (currentStep === 2) {
      // Create trade (skip if empty)
      if (tradeForm.currency_pair && tradeForm.risk_amount) {
        // Use the first available strategy
        const strategyId =
          strategies?.[0]?.id || (await handleCreateStrategy())?.id;
        if (strategyId) await handleCreateTrade(strategyId);
      }
      setCurrentStep(3); // Move to tour
    } else {
      handleNext();
    }
  }, [currentStep, strategies, tradeForm, handleCreateStrategy, handleCreateTrade, handleNext]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleSkipToEnd()}>
      <DialogContent
        className="sm:max-w-lg p-0 overflow-hidden border-border/50"
        data-testid="onboarding-dialog"
      >
        {/* Progress Bar */}
        <div className="h-1 bg-secondary/50">
          <motion.div
            className="h-full bg-gradient-to-r from-[#1877F2] to-blue-600"
            initial={{ width: 0 }}
            animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <div className="p-6">
          <StepIndicator currentStep={currentStep} totalSteps={steps.length} />

          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {currentStep === 0 && <WelcomeStep />}
              {currentStep === 1 && (
                <StrategyStep
                  form={strategyForm}
                  setForm={setStrategyForm}
                  errors={strategyErrors}
                />
              )}
              {currentStep === 2 && (
                <TradeStep tradeForm={tradeForm} setTradeForm={setTradeForm} />
              )}
              {currentStep === 3 && <TourStep />}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <Button
              variant="ghost"
              onClick={currentStep === 0 ? handleSkipToEnd : handleBack}
              className="text-muted-foreground"
              data-testid="onboarding-back"
            >
              {currentStep === 0 ? "Skip All" : (
                <>
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </>
              )}
            </Button>
            <Button
              onClick={handleFinishStep}
              className="rounded-full bg-gradient-to-r from-[#1877F2] to-blue-600 hover:from-blue-600 hover:to-[#1877F2] text-white px-6"
              disabled={creating}
              data-testid="onboarding-next"
            >
              {creating ? (
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>Creating...</span>
                </div>
              ) : currentStep === steps.length - 1 ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Get Started
                </>
              ) : currentStep === 1 ? (
                <>
                  Create & Continue
                  <ArrowRight className="h-4 w-4 ml-1" />
                </>
              ) : currentStep === 2 ? (
                <>
                  {tradeForm.currency_pair ? "Save & Continue" : "Skip Trade"}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
